import { API_BASE_URL } from '../config/constants';

/**
 * Base API service for making HTTP requests
 */
class ApiService {
  constructor(baseURL) {
    this.baseURL = baseURL;
  }

  /**
   * Make a fetch request with authorization
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Fetch options
   * @param {string} token - Authorization token
   * @returns {Promise<Object>}
   */
  async request(endpoint, options = {}, token = null) {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      ...options,
      headers
    };

    try {
      const response = await fetch(url, config);
      
      // Handle different response statuses
      if (response.status === 401) {
        throw new Error('UNAUTHORIZED');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint, token = null) {
    return this.request(endpoint, { method: 'GET' }, token);
  }

  /**
   * POST request
   */
  async post(endpoint, data, token = null) {
    return this.request(
      endpoint,
      {
        method: 'POST',
        body: JSON.stringify(data)
      },
      token
    );
  }

  /**
   * PUT request
   */
  async put(endpoint, data, token = null) {
    return this.request(
      endpoint,
      {
        method: 'PUT',
        body: JSON.stringify(data)
      },
      token
    );
  }

  /**
   * DELETE request
   */
  async delete(endpoint, token = null) {
    return this.request(endpoint, { method: 'DELETE' }, token);
  }
}

export const apiService = new ApiService(API_BASE_URL);

